// src/store/notifications/index.js

import axios from "axios";
import { FCM } from "@capacitor-community/fcm";

const Notifications = {
  namespaced: true,
  state: {
    channels: [
      {
        id: "common",
        name: "Alerts",
        subscribed: undefined,
      },
      {
        id: "news_releases",
        name: "News Release",
        subscribed: undefined,
      },
      {
        id: "garbage_push",
        name: "Garbage Collection",
        subscribed: undefined,
      },
    ],
    fcmToken: null,
  },
  /**
   * Getters to access state
   * @param {object} state
   *
   * @returns {Array} of channels
   */
  getters: {
    /**
     * Returns channels
     * @param {object} state
     * @returns {Array} of channels
     */
    getChannels: (state) => {
      return state.channels;
    },
    /**
     * Returns FCM token
     * @param {object} state
     * @returns {string} fcmToken
     */
    getFcmToken: (state) => {
      return state.fcmToken;
    },
  },

  mutations: {
    /**
     *
     * Subscribe to particular channel
     * @param {object} state
     * @param {object} channelData
     */
    setSubscribeToChannel(state, channelData) {
      var found = state.channels.findIndex((x) => x.id === channelData.channel.id);
      state.channels[found].subscribed = channelData.status;
    },
    /**
     * Set FCM token
     * @param {object} state
     * @param {string} token
     */
    setFcmToken(state, token) {
      state.fcmToken = token;
    },
    /**
     * Update channel subscription status
     * @param {object} state
     * @param {object} payload
     * @param {string} payload.channelId
     * @param {boolean} payload.status
     */
    updateChannelSubscription(state, { channelId, status }) {
      const channel = state.channels.find((channel) => channel.id === channelId);
      if (channel) {
        channel.subscribed = status;
      }
    },
  },

  actions: {
    /**
     * Fetch FCM token and commit to state
     * @param {object} context
     */
    async fetchFcmToken({ commit }) {
      try {
        const token = await FCM.getToken();
        commit("setFcmToken", token);
      } catch (error) {
        console.error("Error fetching FCM token:", error);
      }
    },

    /**
     * Subscribe to garbage push notifications
     * Will create a new AWS Pinpoint Endpoint or update an existing one
     * with the provided deviceToken, garbageDate, garbageDay, recycleDate, recycleDay, source, and recaptchaToken
     * @param {object} context
     * @param {object} payload
     */
    async subscribeToGarbagePushNotifications(
      { commit },
      { deviceToken, garbageDate, garbageDay, recycleDate, recycleDay, source, recaptchaToken, ...params }
    ) {
      const response = await axios.post(process.env.VUE_APP_PUSH_SUBSCRIBE_URL, {
        deviceToken: deviceToken,
        garbageDate: garbageDate,
        garbageDay: garbageDay,
        recycleDate: recycleDate,
        recycleDay: recycleDay,
        source: source,
        recaptchaToken: recaptchaToken, // Add reCAPTCHA token to postData
        ...params,
      });

      if (response.status !== 200) {
        throw Error("Failed to subscribe for garbage push notifications!");
      }
    },

    /**
     * Unsubscribe from garbage push notifications
     * Will delete the AWS Pinpoint Endpoint with the provided deviceToken and recaptchaToken
     * @param {object} context
     * @param {object} payload
     */
    async unsubscribeToGarbagePushNotifications({ commit }, { deviceToken, recaptchaToken }) {
      const response = await axios.post(process.env.VUE_APP_PUSH_UNSUBSCRIBE_URL, {
        deviceToken: deviceToken,
        recaptchaToken: recaptchaToken, // Add reCAPTCHA token to postData
      });

      if (response.status !== 200) {
        throw Error("Failed to unsubscribe for garbage push notifications!");
      }
    },

    /**
     * Update user attributes for garbage/recycle to "None" during Change Address Process
     * Will update the AWS Pinpoint Endpoint with the provided deviceToken and recaptchaToken
     * @param {object} context
     * @param {object} recaptchaToken
     *
     */
    async updateUserAttributesChangeAddress({ getters }, { recaptchaToken }) {
      const pushToken = getters.getFcmToken;
      const garbagePushChannel = getters.getChannels.find((channel) => channel.id === "garbage_push");

      // Check if the user is subscribed to the garbage_push channel
      if (!garbagePushChannel || !garbagePushChannel.subscribed) {
        console.log("User is not subscribed to the garbage_push channel. Skip push notification registration.");
        return;
      }

      const response = await axios.post(process.env.VUE_APP_PUSH_SUBSCRIBE_URL, {
        deviceToken: pushToken.token,
        garbageDate: "None",
        garbageDay: "None",
        recycleDate: "None",
        recycleDay: "None",
        source: "Mobile_Device",
        recaptchaToken: recaptchaToken,
      });

      if (response.status !== 200) {
        throw Error("Failed to update user attributes!");
      }
    },
  },
};

export default Notifications;
